import {Widget} from "../components/Common";
import FlexStack from "../components/Common/flex-stack";
import {useSuspenseQuery} from "@tanstack/react-query";
import {AdminBreederService} from "../services/admin-breeder-service";
import {Box, Button, Menu, MenuItem, Modal, Stack, TableContainer, TextField} from "@mui/material";
import React, {useMemo, useState} from "react";
import {DataTable} from "../components/Table/data-table";
import {RegistryService} from "../services/registry-service";
import {BreederAdminController$BreederListJson} from "../api/generated/breeder-controller";
import Tabbed from "../components/Common/tabbed";
import Autocomplete from "@mui/material/Autocomplete";
import {RegistryDto} from "../api/generated/registry-service";
import {InfoBox} from "../components/Common/alerts";
import {LoadingSuspense} from "../components/Common/loading-suspense";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {Document, Link, Page, PDFViewer, Text, View} from "@react-pdf/renderer";
import {Download, MoreVert} from "@mui/icons-material";
import {useNav} from "../components/Common/hooks/use-nav";
import RouteService from "../services/route-service";
import {useAdminSubscriptions} from "../hooks/use-admin-subscriptions";
import FormattedDate from "../components/Common/formatted-date";
import {DateTime} from "luxon";
import {useSubscriptionHistory} from "../hooks/use-subscription";
import {LoadingFallback} from "../components/Common/loading-fallback";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    p: 4,
};

const useBreederList = () => useSuspenseQuery({
    queryKey: ['admin', 'breeder', {list: 'all'}],
    queryFn: async () => await AdminBreederService.list(),
});

const useIndividualBreederList = () => useSuspenseQuery({
    queryKey: ['admin', 'breeder', {list: 'individual'}],
    queryFn: async () => await AdminBreederService.listIndividuals(),
});

const useRegistryBreederList = (registryId: string) => useSuspenseQuery({
    queryKey: ['admin', 'breeder', {list: 'registry', registryId: registryId}],
    queryFn: async () => await AdminBreederService.listOfRegistry(registryId),
});

const useRegistriesList = () => useSuspenseQuery({
    queryKey: ['admin', 'registries', {list: 'all'}],
    queryFn: async () => await RegistryService.findAll(),
});

const containsFilterValue = (filterValue: string) => (breeder: BreederAdminController$BreederListJson) => Object.entries(breeder)
    .filter(([k]) => k !== 'id')
    .map(entry => entry[1])
    .map(value => DateTime.isDateTime(value) ? value.toLocaleString() : value)
    .flatMap(value => typeof value === 'string' ? [value] : Object.values(value))
    // .filter((value): value is string => typeof value === 'string')
    .map(value => String(value).toLowerCase())
    .some(value => value.includes(filterValue));

const SubscriptionHistory = ({id}: { id: string }) => {
    const {history = [], isPending} = useSubscriptionHistory(id);

    if (isPending) {
        return <LoadingFallback/>;
    }

    return (
        <ol>
            {history.map((log) => (
                <li>{log}</li>
            ))}
        </ol>
    );
};

const BreederTable = ({breeders}: { breeders: BreederAdminController$BreederListJson[] }) => {
    const [filter, setFilter] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [selectedBreederId, setSelectedBreederId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const filteredBreeders = useMemo(
        () => breeders.filter(containsFilterValue(filter.toLowerCase())),
        [breeders, filter]);

    const {
        subscriptions = [],
        subscribe,
        isSubscribePending,
        unsubscribe,
        isUnsubscribePending
    } = useAdminSubscriptions();

    const subscriptionsByBreeder = useMemo(
        () => new Map(subscriptions.map(sub => [sub.breederId, sub.subscription])),
        [subscriptions]);

    const selectedSubscription = !!selectedBreederId && subscriptionsByBreeder.get(selectedBreederId);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, breederId: string) => {
        setMenuAnchor(event.currentTarget);
        setSelectedBreederId(breederId);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedBreederId(null);
    };

    const withClose = (action: () => void) => () => {
        action();
        handleMenuClose();
    };

    const handleSubscribe = (breederId: string) => () => {
        const duration = prompt("Abo-Dauer (in Tagen)?", '7');
        if (duration !== null) {
            void subscribe({breederId, duration: Number.parseInt(duration)});
        }
    };

    return (
        <>
            <TextField
                variant="outlined"
                fullWidth
                placeholder="Filter"
                value={filter}
                onChange={ev => setFilter(ev.target.value)}/>

            <p>{filteredBreeders.length}/{breeders.length} Züchter</p>

            <TableContainer>
                <DataTable data={filteredBreeders} columns={[
                    {id: 'name', header: 'Name', accessor: 'name'},
                    {id: 'farmName', header: 'Hofname', accessor: 'farmName'},
                    {id: 'email', header: 'E-Mail', accessor: 'emailAddress', sortBy: e => e},
                    {id: 'signupDate', header: 'Registrierdatum', accessor: 'signupDate', render: d => d.toLocaleString()},
                    {
                        id: 'address',
                        header: 'Adresse',
                        accessor: 'address',
                        sortBy: addr => ([addr.country, addr.state, addr.city, addr.zipCode, addr.street].filter(Boolean).join(',')),
                        render: addr => (
                            <>
                                <div>{addr.street}</div>
                                <div>{String([addr.zipCode + ' ' + addr.city, addr.state, addr.country].filter(Boolean).join(', '))}</div>
                            </>
                        ),
                    },
                    {
                        id: 'subscriptions',
                        header: 'Abo',
                        accessor: row => [row.id, subscriptionsByBreeder.get(row.id)],
                        sortBy: ([, sub]) => sub?.validFrom.toMillis() ?? 0,
                        render: ([id, sub]) => {
                            return (
                                <div>
                                    <Button aria-label="subscription options"
                                            title="Abo-Optionen"
                                            endIcon={<MoreVert/>}
                                            onClick={(ev) => handleMenuOpen(ev, id)}>
                                        {sub
                                            ? <div>
                                                <FormattedDate date={sub.validFrom}/> - <FormattedDate date={sub.expiresAt}/>
                                                {' '}
                                                {sub.type} {sub.isExpired && 'EXPIRED'}
                                            </div>
                                            : <>Kein Abo</>}
                                    </Button>
                                </div>
                            );
                        }
                    }
                ]}/>
            </TableContainer>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                {!!selectedBreederId && <>
                    {selectedSubscription
                        ? <MenuItem disabled={isUnsubscribePending} onClick={withClose(() => void unsubscribe(selectedBreederId))}>Abo entfernen</MenuItem>
                        : <MenuItem disabled={isSubscribePending} onClick={withClose(handleSubscribe(selectedBreederId))}>Abo hinzufügen</MenuItem>}
                    <MenuItem onClick={() => setModalOpen(true)}>Abo-History anzeigen</MenuItem>
                </>}
            </Menu>

            <Modal open={modalOpen} onClose={() => {
                setModalOpen(false);
                handleMenuClose();
            }}>
                <Box sx={modalStyle}>
                    {!!selectedBreederId && <SubscriptionHistory id={selectedBreederId}/>}
                </Box>
            </Modal>
        </>
    );
};

const AssociationBreederTable = ({registryId}: { registryId: string }) => {
    const {data: breeders} = useRegistryBreederList(registryId);
    const nav = useNav();

    return (
        <Stack>
            <BreederTable breeders={breeders}/>
            <Box sx={{textAlign: 'right'}}>
                <Button variant="contained" startIcon={<Download/>} onClick={() => nav.push(RouteService.ADMIN_BREEDER_PDF, {registryId})}>
                    Download as PDF
                </Button>
            </Box>
        </Stack>
    );
};

const AssociationBreeders = ({registries}: { registries: RegistryDto[] }) => {
    const [selectedRegistry, setSelectedRegistry] = useState(registries[0]);

    return (
        <>
            <Autocomplete
                disablePortal
                disableClearable
                options={registries}
                value={selectedRegistry}
                getOptionLabel={option => `${option.name}, ${option.membersCount} members`}
                onChange={(event, value) => setSelectedRegistry(value)}
                renderInput={(params) => <TextField {...params} variant="outlined" label="Registry"/>}/>

            <LoadingSuspense>
                <AssociationBreederTable registryId={selectedRegistry.id}/>
            </LoadingSuspense>
        </>
    );
};

export const AdminBreedersList = () => {
    const {data: breeders} = useBreederList();
    const {data: registries} = useRegistriesList();
    const {data: individualBreeders} = useIndividualBreederList();

    return (
        <FlexStack>
            <Widget displayName="Züchter">
                <Tabbed>
                    <Tabbed.Tab label={`All ${breeders.length}`}>
                        <BreederTable breeders={breeders}/>
                    </Tabbed.Tab>
                    <Tabbed.Tab label={`Associations ${registries.length}`}>
                        <LoadingSuspense>
                            {registries.length
                                ? <AssociationBreeders registries={registries}/>
                                : <InfoBox>No associations configured</InfoBox>}
                        </LoadingSuspense>
                    </Tabbed.Tab>
                    <Tabbed.Tab label={`Individual users ${individualBreeders.length}`}>
                        <BreederTable breeders={individualBreeders}/>
                    </Tabbed.Tab>
                </Tabbed>
            </Widget>
        </FlexStack>
    );
};

const DATE_LOCALE = 'de-DE';
const DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {day: '2-digit', month: '2-digit', year: 'numeric'};
const toDateString = (d: Date) => d.toLocaleDateString(DATE_LOCALE, DATE_LOCALE_OPTIONS);

export const AdminBreedersPdf = withRouter(({match}: RouteComponentProps<{ registryId: string }>) => {
    const {registryId} = match.params

    const {data: breeders} = useRegistryBreederList(registryId);
    const {data: registries} = useRegistriesList();

    const registry = registries.find(r => r.id === registryId);
    const todaysDate = toDateString(new Date());

    return (
        <PDFViewer width="100%" height="640">
            <Document>
                <Page size="A4" style={{padding:'2cm'}}>
                    <Text style={{textAlign:'center', marginBottom:'8mm'}}>{registry?.name}: {breeders.length} Züchter</Text>

                    {breeders.map((breeder, idx) => {
                        const address = breeder.address;
                        const signupDate = breeder.signupDate.setLocale(DATE_LOCALE).toLocaleString(DATE_LOCALE_OPTIONS);
                        return (
                            <View key={breeder.id} style={{fontSize: '10px', marginBottom:'4mm'}} wrap={false}>
                                <View style={{flexDirection: 'row'}}>
                                    <Text style={{width: '1cm', textAlign: 'right', marginRight: '2mm'}}>{idx+1}.</Text>
                                    <View style={{flexDirection: 'column'}}>
                                        <Text>{breeder.name}, {breeder.farmName}, {breeder.emailAddress}</Text>
                                        <Text>{String([address.street, address.zipCode + ' ' + address.city, address.state, address.country].filter(Boolean).join(', '))}</Text>
                                        <Text>Registriert seit {signupDate === '01.01.1970' ? '01.01.2025' : signupDate}</Text>
                                    </View>
                                </View>
                            </View>
                        );
                    })}


                    <View fixed>
                        <View style={{flexDirection: 'row', justifyContent: 'space-between', fontSize: 10}}>
                            <Text>{todaysDate}</Text>

                            <Text><Link src="https://herds.alpacuna.com/">https://herds.alpacuna.com</Link></Text>

                            <Text style={{textAlign: 'left'}}
                                  render={({pageNumber, totalPages}) => `Seite ${pageNumber} / ${totalPages}`}/>
                        </View>
                    </View>
                </Page>
            </Document>
        </PDFViewer>
    );
});